import * as THREE from 'three';

import { useRef, useState } from 'react';

import { useFrame } from '@react-three/fiber';

type GrokLogoProps = {
  position: [number, number, number];
  scale?: number;
  rotation?: [number, number, number];
};

export function GrokLogo({
  position = [0, 0, 0],
  scale = 1,
  rotation = [0, 0, 0],
}: GrokLogoProps) {
  const logoRef = useRef<THREE.Group>(null);
  const [hover, setHover] = useState(false);

  // Supabase colors
  const GROK_BLACK = '#000000';

  // Subtle animation
  useFrame((state) => {
    if (logoRef.current && hover) {
      // Gentle pulse effect when hovered
      const pulseScale = 1 + Math.sin(state.clock.getElapsedTime() * 3) * 0.02;
      logoRef.current.scale.set(pulseScale, pulseScale, pulseScale);
    }
  });

  return (
    <group
      position={position}
      rotation={rotation}
      scale={scale}
      onPointerOver={() => setHover(true)}
      onPointerOut={() => setHover(false)}
    >
      {/* Logo group */}
      <group ref={logoRef} position={[0, 0.03, 0]}>
        {/* Outer ring - thinner and more precise than before */}
        <mesh position={[0, 0, 0.01]} castShadow>
          <ringGeometry args={[0.32, 0.4, 32]} />
          <meshStandardMaterial color={GROK_BLACK} side={THREE.DoubleSide} />
        </mesh>

        {/* Create the diagonal lightning-like line */}
        <mesh
          position={[0, 0, 0.015]}
          rotation={[0, 0, Math.PI / 4]}
          castShadow
        >
          {/* Use a custom shape for the lightning bolt effect */}
          <shapeGeometry args={[createLightningShape()]} />
          <meshStandardMaterial color={GROK_BLACK} side={THREE.DoubleSide} />
        </mesh>
      </group>

      {/* Glow effect when hovered */}
      {hover && (
        <mesh position={[0, 0.01, -0.01]} receiveShadow>
          <circleGeometry args={[0.8, 32]} />
          <meshStandardMaterial
            color={GROK_BLACK}
            transparent
            opacity={0.1}
            emissive={GROK_BLACK}
            emissiveIntensity={0.2}
          />
        </mesh>
      )}
    </group>
  );
}

// Helper function to create the lightning-like diagonal shape
function createLightningShape() {
  const shape = new THREE.Shape();

  // Starting point at top-left
  shape.moveTo(-0.6, 0.08);

  // Top edge sloping down to the right
  shape.lineTo(0.6, 0.08);

  // Right edge
  shape.lineTo(0.6, 0.03);

  // Bottom edge sloping up to the left
  shape.lineTo(-0.6, -0.08);

  // Left edge
  shape.lineTo(-0.6, -0.03);

  // Close the shape
  shape.lineTo(-0.6, 0.08);

  return shape;
}
